import { openDB } from 'idb';

const DB_NAME = 'fast-pos-offline';
const DB_VERSION = 1;

// Object stores:
//   menu_cache    — keyed by outlet_id, value = { categories, items, modifiers, tables, fetched_at }
//   pending_orders — keyed by local_id (auto), each pending OrderCreate payload + retry meta
//   meta          — generic key/value (last sync time, etc.)

let dbPromise = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('menu_cache')) {
          db.createObjectStore('menu_cache', { keyPath: 'outlet_id' });
        }
        if (!db.objectStoreNames.contains('pending_orders')) {
          const store = db.createObjectStore('pending_orders', {
            keyPath: 'local_id',
            autoIncrement: true,
          });
          store.createIndex('outlet_id', 'outlet_id', { unique: false });
          store.createIndex('status', 'status', { unique: false });
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta');
        }
      },
    });
  }
  return dbPromise;
}

// ---- Menu cache ----
export async function cacheMenuData(outletId, payload) {
  const db = await getDB();
  await db.put('menu_cache', {
    outlet_id: outletId,
    ...payload,
    fetched_at: new Date().toISOString(),
  });
}

export async function getCachedMenuData(outletId) {
  const db = await getDB();
  return (await db.get('menu_cache', outletId)) || null;
}

// ---- Pending orders queue ----
export async function enqueuePendingOrder(orderPayload) {
  const db = await getDB();
  const entry = {
    ...orderPayload,
    status: 'pending',
    attempts: 0,
    last_error: null,
    queued_at: new Date().toISOString(),
  };
  const id = await db.add('pending_orders', entry);
  return { ...entry, local_id: id };
}

export async function listPendingOrders(outletId = null) {
  const db = await getDB();
  const all = await db.getAll('pending_orders');
  return outletId ? all.filter((o) => o.outlet_id === outletId) : all;
}

export async function markOrderSynced(local_id) {
  const db = await getDB();
  await db.delete('pending_orders', local_id);
}

export async function markOrderFailed(local_id, error) {
  const db = await getDB();
  const entry = await db.get('pending_orders', local_id);
  if (!entry) return;
  entry.attempts = (entry.attempts || 0) + 1;
  entry.last_error = error;
  entry.status = entry.attempts >= 5 ? 'error' : 'pending';
  await db.put('pending_orders', entry);
}

export async function countPendingByOutlet(outletId) {
  const list = await listPendingOrders(outletId);
  return list.length;
}

// ---- Meta ----
export async function setMeta(key, value) {
  const db = await getDB();
  await db.put('meta', value, key);
}

export async function getMeta(key) {
  const db = await getDB();
  return await db.get('meta', key);
}
